import React from "react";
import { Card } from "flowbite-react";
import {
  MENU_CATEGORIES,
  type MenuCategory,
  type MenuItem,
} from "@/constants/Menu";
import { centeredRowStartTrack } from "@/lib/BalancedGrid";

interface ToolCardProps {
  item: MenuItem;
  className: string;
}

const ToolCard: React.FC<ToolCardProps> = ({ item, className }) => {
  return (
    <Card
      className={`w-full overflow-hidden rounded-xl border-border shadow-sm ${className}`}
      href={item.href}
      theme={{ root: { children: "justify-start gap-2 py-4" } }}
      renderImage={() => (
        <img
          src={item.imageSrc}
          alt=""
          width={960}
          height={640}
          loading="lazy"
          decoding="async"
          className="aspect-[3/2] w-full object-cover"
        />
      )}
    >
      <h4 className="text-xl font-bold leading-tight text-primary-700 md:flex md:min-h-[2lh] md:items-center">
        {item.title}
      </h4>
      <p className="text-primary-900">{item.description}</p>
    </Card>
  );
};

const MD_MAX_COLUMNS = 3;
const LG_MAX_COLUMNS = 4;
const MD_COL_START_CLASS_NAMES = [
  "md:col-start-1",
  "md:col-start-2",
  "md:col-start-3",
] as const;
const LG_COL_START_CLASS_NAMES = [
  "lg:col-start-1",
  "lg:col-start-2",
  "lg:col-start-3",
  "lg:col-start-4",
] as const;

const rowStartClassName = (index: number, itemCount: number): string => {
  const mdTrack = centeredRowStartTrack(index, itemCount, MD_MAX_COLUMNS);
  const lgTrack = centeredRowStartTrack(index, itemCount, LG_MAX_COLUMNS);
  const md = mdTrack === undefined ? "" : MD_COL_START_CLASS_NAMES[mdTrack - 1];
  const lg =
    lgTrack === undefined
      ? "lg:col-start-auto"
      : LG_COL_START_CLASS_NAMES[lgTrack - 1];
  return [md, lg].filter((name) => name !== "").join(" ");
};

const ToolCardGrid: React.FC<{ items: readonly MenuItem[] }> = ({ items }) => {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-6 lg:grid-cols-8">
      {items.map((item, index) => (
        <ToolCard
          key={item.href}
          item={item}
          className={`md:col-span-2 ${rowStartClassName(index, items.length)}`}
        />
      ))}
    </div>
  );
};

const CategoryHeading: React.FC<{ category: MenuCategory }> = ({
  category,
}) => {
  return (
    <h3
      id={`tools-${category.id}`}
      className="mb-4 text-center text-2xl font-bold text-primary-700"
    >
      {category.title}
    </h3>
  );
};

const CategorySection: React.FC<{ category: MenuCategory }> = ({
  category,
}) => {
  return (
    <section aria-labelledby={`tools-${category.id}`}>
      <CategoryHeading category={category} />
      <ToolCardGrid items={category.items} />
    </section>
  );
};

const Tools: React.FC = () => {
  return (
    <section id="tools" className="py-8">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold mb-4 text-center text-primary-500">
          <span style={{ fontFamily: "Fredoka, sans-serif" }}>Nekometry</span>
          でできること
        </h2>
        <div className="space-y-10">
          {MENU_CATEGORIES.map((category) => (
            <CategorySection key={category.id} category={category} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Tools;
